import { useState, useEffect, useRef } from 'react'

/**
 * Throttles rapid-fire changes to a value, reporting them at most once per interval.
 * The first value is reported immediately. Subsequent values within the interval
 * are queued and only the latest value is reported after the interval expires.
 * 
 * @param value - The value to throttle
 * @param intervalMs - The minimum time interval between reports in milliseconds (default: 100)
 * @returns The throttled value
 * 
 * @example
 * // If values come in at 10ms intervals:
 * // - First value: reported immediately
 * // - Next 9 values: queued, only latest is kept
 * // - After 100ms: latest value is reported
 */
export function useThrottleChanges<T>(value: T, intervalMs: number = 100): T {
  const [throttledValue, setThrottledValue] = useState<T>(value)
  const lastReportTimeRef = useRef<number>(0)
  const timeoutRef = useRef<number | null>(null)
  const pendingValueRef = useRef<T>(value)

  useEffect(() => {
    const now = Date.now()
    const timeSinceLastReport = now - lastReportTimeRef.current

    pendingValueRef.current = value
    if (timeoutRef.current !== null) {
      // If we have an active timer then we only need to update the pending value

    } else if (timeSinceLastReport < intervalMs) {
      // Not enough time has passed - store the latest value
      const remainingTime = intervalMs - timeSinceLastReport
      timeoutRef.current = window.setTimeout(() => {
        setThrottledValue(pendingValueRef.current)
        lastReportTimeRef.current = Date.now()
        timeoutRef.current = null
      }, remainingTime)

    } else {
      // Enough time has passed, report immediately
      setThrottledValue(value)
      lastReportTimeRef.current = now
      
      // Clear any pending timeout
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }

    // Cleanup on unmount or when value/interval changes
    return () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current)
        timeoutRef.current = null
      }
    }
  }, [value, intervalMs])

  return throttledValue
}

